// Atrapa errores de render para que la app no quede en pantalla blanca.
// Específicamente útil cuando hay chunks lazy desactualizados después de
// un deploy nuevo — el navegador tiene cacheado un chunk que ya no existe
// y el import falla. Detectamos eso y recargamos automáticamente.

import { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, recargando: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Detectar errores típicos de chunk lazy que falla en deploy nuevo
    const msg = String(error?.message ?? '').toLowerCase();
    const esChunkObsoleto =
      msg.includes('loading chunk') ||
      msg.includes('failed to fetch dynamically imported module') ||
      msg.includes('importing a module script failed');

    if (esChunkObsoleto) {
      // Recargar solo una vez para no loopear si el problema persiste
      const yaIntento = sessionStorage.getItem('mezo_reload_done');
      if (!yaIntento) {
        sessionStorage.setItem('mezo_reload_done', '1');
        this.setState({ recargando: true });
        setTimeout(() => window.location.reload(), 100);
        return;
      }
    }
    // Loguear para debug — Sentry lo recogería en producción
    console.error('[ErrorBoundary]', error, info);
  }

  reset = () => {
    sessionStorage.removeItem('mezo_reload_done');
    this.setState({ error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.recargando) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-mezo-ink">
          <div className="w-8 h-8 border-4 border-mezo-gold border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center px-6 bg-mezo-ink">
          <div className="max-w-md w-full bg-mezo-ink-raised border border-mezo-ink-line rounded-mezo-xl p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-mezo-rojo/15 border border-mezo-rojo/30 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-7 h-7 text-mezo-rojo" strokeWidth={1.5} />
            </div>
            <h2 className="text-mezo-cream font-body font-semibold text-lg mb-2">
              Algo se rompió
            </h2>
            <p className="text-mezo-cream-dim font-body text-sm mb-6 leading-relaxed">
              Tuvimos un error inesperado. No se perdió nada — tus datos están seguros.
              Toca para recargar mezo.
            </p>
            <button
              onClick={this.reset}
              className="w-full inline-flex items-center justify-center gap-2 bg-mezo-gold hover:bg-mezo-gold-deep text-mezo-ink font-body font-semibold py-3 rounded-mezo-md text-sm transition"
            >
              <RefreshCw size={14} strokeWidth={2} />
              Recargar
            </button>
            {process.env.NODE_ENV !== 'production' && (
              <pre className="text-left text-xs text-mezo-stone mt-5 max-h-32 overflow-auto bg-mezo-ink p-3 rounded">
                {String(this.state.error)}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
